/**
 * CallScriptPane — the published recruiter Script + Summary, rendered INLINE in
 * Call Mode (not the slide-over drawer, which would cover the prescreen). Reuses
 * the same published-artifact reader the StudyReferenceDrawer uses.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { StudyArtifact } from '../../types';
import MarkdownText from '../MarkdownText';
import { publishedArtifact } from '../studies/TextArtifactPanel';

interface Props {
  artifacts: StudyArtifact[];
  loading?: boolean;
  /**
   * Render ONLY this artifact, without the internal Script/Summary toggle —
   * used by Call Mode, where Script and Summary are promoted to top-level
   * left-column tabs. Omitted elsewhere = the original two-tab reader.
   */
  which?: 'script' | 'summary';
}

export default function CallScriptPane({ artifacts, loading, which }: Props) {
  const [tab, setTab] = useState<'script' | 'summary'>('script');
  const active = which ?? tab;

  const script = publishedArtifact(artifacts || [], 'recruiter_script');
  const summary = publishedArtifact(artifacts || [], 'summary');
  const content = active === 'script' ? script?.content?.text : summary?.content?.text;

  const tabBtn = (key: 'script' | 'summary', label: string) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
        tab === key
          ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {!which && (
        <div className="mb-2 inline-flex gap-1 self-start rounded-lg bg-gray-100 p-1 dark:bg-gray-900/60">
          {tabBtn('script', 'Script')}
          {tabBtn('summary', 'Summary')}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : content ? (
          <MarkdownText text={content} />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No published {active === 'script' ? 'recruiter script' : 'summary'} yet — an admin can generate one under Studies.
          </p>
        )}
      </div>
    </div>
  );
}
